/* @jsxImportSource @emotion/react */

import type { ReactElement, ReactNode } from 'react'
import { css } from '@emotion/react'

import { SMALL_BREAKPOINT } from '../../utils/breakpoints'
import { WebsiteText } from '../WebsiteText'

type TextLayoutProps = {
  type: 'default' | 'centered'
  title: string
  titleMaxWidth?: number
  description?: string
  descriptionMaxWidth?: number
  illustration?: ReactNode
  button?: ReactNode | ReactNode[]
}

export const TextLayout = (props: TextLayoutProps): ReactElement => {
  if (props.type === 'centered') {
    return <TextLayoutCentered {...props} />
  } else {
    return <TextLayoutDefault {...props} />
  }
}

const TextLayoutDefault = ({
  title,
  titleMaxWidth,
  description,
  descriptionMaxWidth,
  illustration,
  button,
}: TextLayoutProps): ReactElement => {
  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
      }}
    >
      {illustration && <div className="margin-bottom-32">{illustration}</div>}
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          gap: 32px;
          width: 100%;

          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            flex-direction: column;
          }
        `}
      >
        <WebsiteText
          variant="title"
          className="shade-00"
          css={css`
            margin-bottom: 32px;

            @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
              margin-bottom: 0;
            }
          `}
          style={{
            maxWidth: titleMaxWidth ?? 'auto',
            marginRight: 'auto',
            textAlign: 'start',
          }}
        >
          {title}
        </WebsiteText>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 32,
            alignItems: 'start',
          }}
        >
          {description && (
            <WebsiteText
              variant="body-lg"
              className="shade-00"
              style={{
                maxWidth: descriptionMaxWidth ?? 'auto',
                marginRight: 'auto',
                textAlign: 'start',
              }}
            >
              {description}
            </WebsiteText>
          )}
          {button && <div className="margin-bottom-60">{button}</div>}
        </div>
      </div>
    </div>
  )
}

const TextLayoutCentered = ({
  title,
  titleMaxWidth,
  description,
  descriptionMaxWidth,
  illustration,
  button,
}: TextLayoutProps): ReactElement => {
  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {illustration && (
        <div className="margin-bottom-32" style={{ width: 'fit-content' }}>
          {illustration}
        </div>
      )}
      <WebsiteText
        variant="title"
        className="margin-bottom-32 shade-00"
        style={{
          maxWidth: titleMaxWidth ?? 'auto',
          marginRight: 'auto',
          marginLeft: 'auto',
          textAlign: 'center',
        }}
      >
        {title}
      </WebsiteText>
      {description && (
        <WebsiteText
          variant="body-lg"
          className="margin-bottom-32 shade-00"
          style={{
            maxWidth: descriptionMaxWidth ?? 'auto',
            marginRight: 'auto',
            marginLeft: 'auto',
            textAlign: 'center',
          }}
        >
          {description}
        </WebsiteText>
      )}
      {button && <div className="margin-bottom-60">{button}</div>}
    </div>
  )
}
